/**
 * Central SEO utilities: title/description, canonical, hreflang, JSON-LD.
 * All public pages must go through setSeoMeta / applyRouteSeo.
 */

export const SITE_URL = 'https://manga.ko3672.com'
export const DEFAULT_OG_IMAGE = `${SITE_URL}/web_SEO.png`
export const DEFAULT_LOCALE = 'zh'

export const SUPPORTED_LANGS = ['zh', 'en', 'ja', 'ko', 'zh-TW']

export const LANG_TO_HTML = {
  zh: 'zh-CN',
  en: 'en',
  ja: 'ja',
  ko: 'ko',
  'zh-TW': 'zh-TW',
}

/** Map route locale param → hreflang code */
export const LANG_TO_HREFLANG = {
  zh: 'zh-CN',
  en: 'en',
  ja: 'ja',
  ko: 'ko',
  'zh-TW': 'zh-Hant',
}

function stripTrailingSlash(url) {
  return url.length > 1 ? url.replace(/\/+$/, '') : url
}

/** Absolute site URL for a locale + path. path is like '' | 'MangaTranslation' | 'faq' */
export function buildCanonicalUrl(lang, path = '') {
  const locale = SUPPORTED_LANGS.includes(lang) ? lang : DEFAULT_LOCALE
  const clean = String(path || '').replace(/^\/+/, '').replace(/\/+$/, '')
  return stripTrailingSlash(`${SITE_URL}/${locale}${clean ? `/${clean}` : ''}`)
}

/** Sibling path shared by all locales, e.g. '' | 'MangaTranslation' | 'faq' */
export function getPagePathKey(routePath = window.location.pathname) {
  const parts = routePath.split('/').filter(Boolean)
  if (!parts.length) return ''
  if (SUPPORTED_LANGS.includes(parts[0])) {
    return parts.slice(1).join('/')
  }
  return parts.join('/')
}

function upsertMeta({ name, property, content }) {
  const selector = name ? `meta[name="${name}"]` : `meta[property="${property}"]`
  let el = document.querySelector(selector)
  if (!el) {
    el = document.createElement('meta')
    if (name) el.setAttribute('name', name)
    if (property) el.setAttribute('property', property)
    document.head.appendChild(el)
  }
  el.setAttribute('content', content)
}

function upsertCanonical(href) {
  let el = document.querySelector('link[rel="canonical"]')
  if (!el) {
    el = document.createElement('link')
    el.setAttribute('rel', 'canonical')
    document.head.appendChild(el)
  }
  el.setAttribute('href', href)
}

/**
 * Replace all hreflang alternates for the current page path.
 * @param {string} pathKey path without locale prefix
 * @param {string} xDefault absolute URL for x-default (usually default locale)
 */
export function setHreflangAlternates(pathKey = '', xDefault = null) {
  document.querySelectorAll('link[rel="alternate"][hreflang]').forEach((n) => n.remove())

  const variants = SUPPORTED_LANGS.map((lang) => ({
    lang,
    hreflang: LANG_TO_HREFLANG[lang],
    href: buildCanonicalUrl(lang, pathKey),
  }))

  variants.forEach(({ hreflang, href }) => {
    const link = document.createElement('link')
    link.setAttribute('rel', 'alternate')
    link.setAttribute('hreflang', hreflang)
    link.setAttribute('href', href)
    document.head.appendChild(link)
  })

  const defaultHref = xDefault || buildCanonicalUrl(DEFAULT_LOCALE, pathKey)
  const x = document.createElement('link')
  x.setAttribute('rel', 'alternate')
  x.setAttribute('hreflang', 'x-default')
  x.setAttribute('href', defaultHref)
  document.head.appendChild(x)
}

export function setJsonLd(id, data) {
  let el = document.getElementById(id)
  if (!el) {
    el = document.createElement('script')
    el.setAttribute('type', 'application/ld+json')
    el.id = id
    document.head.appendChild(el)
  }
  el.textContent = JSON.stringify(data)
}

export function removeJsonLd(id) {
  const el = document.getElementById(id)
  if (el) el.remove()
}

/**
 * Full page SEO. Prefer this over mutating document.title directly.
 * @param {object} opts
 * @param {string} opts.title
 * @param {string} opts.description
 * @param {string} [opts.lang] route locale param; defaults to html lang mapping
 * @param {string} [opts.pathKey] path without locale; auto from location if omitted
 * @param {string} [opts.image]
 * @param {string} [opts.type]
 * @param {'index,follow'|'noindex,follow'|'noindex,nofollow'} [opts.robots]
 * @param {object|object[]} [opts.jsonLd] structured data payload(s)
 * @param {string} [opts.jsonLdId]
 */
export function setSeoMeta({
  title,
  description,
  lang,
  pathKey,
  image = DEFAULT_OG_IMAGE,
  type = 'website',
  robots = 'index,follow',
  jsonLd = null,
  jsonLdId = 'page-jsonld',
} = {}) {
  const routeLang = lang || detectLangFromPath()
  const pagePath = pathKey !== undefined ? pathKey : getPagePathKey()
  const canonical = buildCanonicalUrl(routeLang, pagePath)

  document.title = title
  if (LANG_TO_HTML[routeLang]) {
    document.documentElement.lang = LANG_TO_HTML[routeLang]
  }

  upsertMeta({ name: 'description', content: description })
  upsertMeta({ name: 'robots', content: robots })
  upsertMeta({ property: 'og:title', content: title })
  upsertMeta({ property: 'og:description', content: description })
  upsertMeta({ property: 'og:image', content: image })
  upsertMeta({ property: 'og:type', content: type })
  upsertMeta({ property: 'og:url', content: canonical })
  upsertMeta({ property: 'og:locale', content: LANG_TO_HTML[routeLang] || 'zh-CN' })
  upsertMeta({ property: 'og:site_name', content: 'KokeManga' })
  upsertMeta({ name: 'twitter:card', content: 'summary_large_image' })
  upsertMeta({ name: 'twitter:title', content: title })
  upsertMeta({ name: 'twitter:description', content: description })
  upsertMeta({ name: 'twitter:image', content: image })

  upsertCanonical(canonical)
  setHreflangAlternates(pagePath)

  if (jsonLd) {
    setJsonLd(jsonLdId, jsonLd)
  } else {
    removeJsonLd(jsonLdId)
  }
}

function detectLangFromPath() {
  const parts = window.location.pathname.split('/').filter(Boolean)
  return SUPPORTED_LANGS.includes(parts[0]) ? parts[0] : DEFAULT_LOCALE
}

export function getLangFromRoute(route) {
  const lang = route?.params?.lang
  return SUPPORTED_LANGS.includes(lang) ? lang : detectLangFromPath()
}

/** SoftwareApplication + WebSite graph for the product landing */
export function buildSoftwareJsonLd({ lang, pathKey, title, description }) {
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebSite',
        '@id': `${SITE_URL}/#website`,
        name: 'KokeManga',
        url: `${SITE_URL}/`,
        inLanguage: Object.values(LANG_TO_HREFLANG),
        description: 'Free online AI manga translation tool.',
      },
      {
        '@type': 'SoftwareApplication',
        name: 'KokeManga',
        applicationCategory: 'MultimediaApplication',
        operatingSystem: 'Web',
        url: buildCanonicalUrl(lang, pathKey),
        description,
        offers: {
          '@type': 'Offer',
          price: '0',
          priceCurrency: lang === 'zh' || lang === 'zh-TW' ? 'CNY' : 'USD',
        },
        featureList: [
          'AI manga translation',
          'Batch upload',
          'Original rendering',
          'Multi-format export',
        ],
      },
    ],
  }
}

export function buildFaqJsonLd(faqItems = []) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map((item) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.a,
      },
    })),
  }
}

export function buildArticleJsonLd({ title, description, lang, pathKey, dateModified = '2026-01-01' }) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: title,
    description,
    inLanguage: LANG_TO_HTML[lang] || 'zh-CN',
    url: buildCanonicalUrl(lang, pathKey),
    dateModified,
    author: {
      '@type': 'Organization',
      name: 'KokeManga',
    },
    publisher: {
      '@type': 'Organization',
      name: 'KokeManga',
      logo: {
        '@type': 'ImageObject',
        url: DEFAULT_OG_IMAGE,
      },
    },
  }
}

export function buildBreadcrumbJsonLd(lang, pathKey, name) {
  const items = [
    {
      '@type': 'ListItem',
      position: 1,
      name: 'KokeManga',
      item: buildCanonicalUrl(lang, ''),
    },
  ]
  if (pathKey) {
    items.push({
      '@type': 'ListItem',
      position: 2,
      name,
      item: buildCanonicalUrl(lang, pathKey),
    })
  }
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items,
  }
}
